// Mock data and functions for GetCare

export const mockStats = {
  ndisParticipants: "20,000+",
  registeredProviders: "20,000+",
  marketSize: "$40B+",
  complianceRate: "99.8%"
};

export const mockMarketStats = {
  participants: 20000,
  providers: 20000,
  marketValue: 40,
  growthRate: 25
};

export const mockTestimonials = [
  {
    id: 1,
    name: "Sarah Mitchell",
    role: "CEO, CareFirst NDIS",
    company: "Melbourne, VIC",
    text: "GetCare transformed our compliance workflow. We went from weeks of audit preparation to being ready in days. The AI alerts have prevented several potential compliance issues.",
    rating: 5
  },
  {
    id: 2,
    name: "David Chen",
    role: "Operations Manager",
    company: "Sydney Community Care",
    text: "The custom-built approach meant the system actually works the way we work, not the other way around. Staff adoption was seamless.",
    rating: 5
  },
  {
    id: 3,
    name: "Emma Thompson",
    role: "Quality & Compliance Lead",
    company: "Brisbane SIL Services",
    text: "Having our incident management, staff tracking, and compliance dashboard all in one custom platform has been a game-changer. The predictive analytics help us stay ahead.",
    rating: 5
  }
];

export const mockFeatures = [
  {
    id: 1,
    title: "Participant Management",
    description: "Complete digital participant profiles, goals, funding tracking and documentation.",
    icon: "Users"
  },
  {
    id: 2,
    title: "Staff Management",
    description: "Credential tracking, onboarding, shift visibility and compliance monitoring.",
    icon: "UserCheck"
  },
  {
    id: 3,
    title: "Service Agreement Automation",
    description: "Generate agreements automatically using your business profile and templates.",
    icon: "FileText"
  },
  {
    id: 4,
    title: "Incident Reporting",
    description: "Structured incident capture with escalation triggers and compliance logging.",
    icon: "AlertTriangle"
  },
  {
    id: 5,
    title: "Progress Notes & Case Logs",
    description: "Digital case notes, progress tracking, and documentation management.",
    icon: "FileEdit"
  },
  {
    id: 6,
    title: "Compliance Dashboard",
    description: "Real-time compliance monitoring, audit readiness, and automated alerts.",
    icon: "Shield"
  },
  {
    id: 7,
    title: "Finance & Invoicing",
    description: "NDIS-ready invoicing, funding visibility and reporting.",
    icon: "DollarSign"
  },
  {
    id: 8,
    title: "AI Reporting & Predictive Analytics",
    description: "AI-powered insights, forecasting, and executive reporting automation.",
    icon: "Brain"
  },
  {
    id: 9,
    title: "Digital Onboarding & Contracts",
    description: "Automated participant and staff onboarding with digital signatures.",
    icon: "ClipboardCheck"
  },
  {
    id: 10,
    title: "Mobile App for Support Workers",
    description: "Secure mobile access for notes, shifts, updates and incident reporting.",
    icon: "Smartphone"
  }
];

export const mockAIFeatures = [
  {
    id: 1,
    title: "AI Compliance Engine",
    description: "Automatically monitors documentation, staff credentials, incidents and reporting.",
    benefit: "Stay audit-ready 24/7"
  },
  {
    id: 2,
    title: "Incident Pattern Detection",
    description: "Identifies recurring trends and flags high-risk areas.",
    benefit: "Prevent issues before they escalate"
  },
  {
    id: 3,
    title: "Predictive Workforce Planning",
    description: "Forecast staffing shortages before they happen.",
    benefit: "Optimize resource allocation"
  },
  {
    id: 4,
    title: "AI Executive Reports",
    description: "Generate management-ready summaries in seconds.",
    benefit: "Save hours on reporting"
  },
  {
    id: 5,
    title: "Smart Notifications",
    description: "Receive alerts for expiring credentials, missing documentation or compliance gaps.",
    benefit: "Never miss critical deadlines"
  }
];

export const mockPricing = [
  {
    id: 1,
    tier: "Small Providers",
    subtitle: "5-15 Staff",
    buildPrice: "Starting from $7,500",
    hostingPrice: "$299/month",
    features: [
      "Custom platform build",
      "Core compliance features",
      "Staff & participant management",
      "Basic AI alerts",
      "Email support",
      "Australian hosting"
    ],
    cta: "Request Proposal"
  },
  {
    id: 2,
    tier: "Medium Providers",
    subtitle: "15-50 Staff",
    buildPrice: "Starting from $15,000",
    hostingPrice: "$599/month",
    features: [
      "Custom platform build",
      "Full AI compliance engine",
      "Advanced incident management",
      "Predictive analytics",
      "Integration support",
      "Priority support",
      "Private hosting option"
    ],
    aiAddon: "AI Add-on: $199-$499/month",
    cta: "Request Proposal",
    featured: true
  },
  {
    id: 3,
    tier: "Enterprise Providers",
    subtitle: "50+ Staff",
    buildPrice: "Starting from $35,000",
    hostingPrice: "Custom pricing",
    features: [
      "Dedicated infrastructure",
      "Advanced AI engine",
      "White-label options",
      "Multi-site management",
      "Custom integrations",
      "24/7 priority support",
      "Dedicated account manager"
    ],
    aiAddon: "Full AI suite included",
    cta: "Book Strategy Call"
  }
];

export const mockTechStack = [
  {
    category: "Frontend",
    technologies: ["React 19", "Next.js", "Tailwind CSS", "TypeScript"],
    icon: "Monitor"
  },
  {
    category: "Mobile",
    technologies: ["Flutter", "Native iOS/Android", "Offline-first"],
    icon: "Smartphone"
  },
  {
    category: "Backend",
    technologies: ["Node.js", "Express", "MongoDB", "PostgreSQL"],
    icon: "Server"
  },
  {
    category: "AI & Analytics",
    technologies: ["OpenAI APIs", "Custom ML Models", "Predictive Engine"],
    icon: "Brain"
  },
  {
    category: "Infrastructure",
    technologies: ["AWS", "VPS Options", "Docker", "Kubernetes"],
    icon: "Cloud"
  },
  {
    category: "Security",
    technologies: ["End-to-end Encryption", "SOC 2", "NDIS Compliant"],
    icon: "Lock"
  }
];

export const mockComparison = {
  categories: ["Customization", "AI Features", "Ownership", "Pricing", "Support", "Deployment"],
  getCare: [
    "Fully custom-built per provider",
    "AI embedded at system level",
    "Full ownership option",
    "One-time build + hosting",
    "Direct local support",
    "2-6 weeks"
  ],
  genericSaas: [
    "Fixed templates only",
    "Basic reporting only",
    "Subscription only",
    "Per-user monthly fees",
    "Ticket-based support",
    "Instant but limited"
  ]
};

export const mockPartnerProgram = [
  {
    type: "NDIS Consultants",
    commission: "15-20% revenue share",
    benefits: ["White-label option", "Co-branded materials", "Priority implementation"],
    icon: "Briefcase"
  },
  {
    type: "Accountants",
    commission: "10-15% referral fee",
    benefits: ["Financial integration", "Automated invoicing", "Dedicated support"],
    icon: "Calculator"
  },
  {
    type: "Disability Lawyers",
    commission: "Custom partnership",
    benefits: ["Compliance automation", "Audit trail features", "Legal documentation"],
    icon: "Scale"
  },
  {
    type: "HR Providers",
    commission: "Custom partnership",
    benefits: ["Staff management module", "Credential tracking", "Training integration"],
    icon: "Users"
  }
];

export const mockComplianceItems = [
  "Version-controlled policy storage",
  "Incident documentation logging",
  "Automated compliance scoring",
  "NDIA-ready report exports",
  "Staff credential monitoring",
  "Audit trail tracking",
  "Real-time compliance dashboard"
];

export const mockFAQs = [
  {
    id: 1,
    question: "How is GetCare different from other NDIS software?",
    answer: "We don't sell a one-size-fits-all SaaS subscription. GetCare is custom-built for your specific operational workflow, with AI embedded at the system level. You get a platform that adapts to you, not the other way around."
  },
  {
    id: 2,
    question: "How long does implementation take?",
    answer: "Implementation timeframes vary based on your organisation size and requirements. Small providers typically go live in 6-8 weeks, while enterprise implementations may take 12-16 weeks. We work closely with your team throughout the process."
  },
  {
    id: 3,
    question: "Is my data secure and compliant?",
    answer: "Absolutely. We implement enterprise-grade security including end-to-end encryption, role-based access control, and secure Australian hosting options. Our platform is designed with NDIS compliance requirements in mind."
  },
  {
    id: 4,
    question: "Can you integrate with our existing systems?",
    answer: "Yes. Our custom-built approach allows us to integrate with your existing accounting software, rostering systems, or other tools. We'll discuss your integration needs during the strategy session."
  },
  {
    id: 5,
    question: "What kind of support do you provide?",
    answer: "All clients receive ongoing technical support from our Australian team. Support levels vary by plan, with enterprise clients receiving 24/7 priority support and a dedicated account manager."
  },
  {
    id: 6,
    question: "Do I need technical expertise to use the system?",
    answer: "No. While GetCare is technically sophisticated behind the scenes, the interface is designed to be intuitive for care staff. We provide comprehensive training and documentation for your team."
  }
];

// API form submission handlers
const API_URL = process.env.REACT_APP_BACKEND_URL || '';

export const handleDemoRequest = async (formData) => {
  try {
    const response = await fetch(`${API_URL}/api/demo-request`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(formData),
    });
    
    if (!response.ok) {
      throw new Error('Failed to submit demo request');
    }
    
    return await response.json();
  } catch (error) {
    console.error("Demo request error:", error);
    throw error;
  }
};

export const handleContactSubmission = async (formData) => {
  try {
    const response = await fetch(`${API_URL}/api/contact`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(formData),
    });
    
    if (!response.ok) {
      throw new Error('Failed to submit contact form');
    }
    
    return await response.json();
  } catch (error) {
    console.error("Contact form error:", error);
    throw error;
  }
};

export const handleNewsletterSubscription = async (email) => {
  try {
    const response = await fetch(`${API_URL}/api/newsletter`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ email }),
    });
    
    if (!response.ok) {
      throw new Error('Failed to subscribe to newsletter');
    }
    
    return await response.json();
  } catch (error) {
    console.error("Newsletter subscription error:", error);
    throw error;
  }
};

export const handleResourceDownload = async (resourceType) => {
  console.log("Resource download requested:", resourceType);
  
  return new Promise((resolve) => {
    setTimeout(() => {
      resolve({
        success: true,
        message: `Your ${resourceType} is being prepared for download. Check your email!`
      });
    }, 1000);
  });
};
